import { create } from "zustand";
import { api, ApiProblem } from "../services/api";
import type { Coordinate, Day, Navigation, Place, Stop, TravelMode, TripDocument, TripSummary } from "../types";

type SaveState = "saved" | "dirty" | "saving" | "offline" | "conflict";
export type ElevationLookupState = "loading" | "failed";

interface EditorState {
  summaries: TripSummary[];
  document: TripDocument | null;
  revision: string;
  selectedDayId: string | null;
  selectedPlaceId: string | null;
  history: TripDocument[];
  future: TripDocument[];
  saveState: SaveState;
  dirtyDuringSave: boolean;
  loading: boolean;
  error: string | null;
  elevationLookups: Record<string, ElevationLookupState>;
  load: (preferredId?: string) => Promise<void>;
  createTrip: (name: string, startDate?: string, mode?: TravelMode) => Promise<void>;
  duplicateTrip: () => Promise<void>;
  deleteTrip: () => Promise<void>;
  openTrip: (id: string) => Promise<void>;
  selectDay: (id: string) => void;
  selectPlace: (id: string | null) => void;
  updatePlace: (id: string, patch: Partial<Place>) => void;
  refreshElevation: (id: string) => void;
  addPlace: (place: Place, dayId?: string) => void;
  removeStop: (dayId: string, index: number) => void;
  reorderStop: (dayId: string, from: number, to: number) => void;
  updateStop: (dayId: string, index: number, patch: Partial<Stop>) => void;
  updateDay: (dayId: string, patch: Partial<Day>) => void;
  addDay: () => void;
  moveDay: (dayId: string, direction: -1 | 1) => void;
  deleteDay: (dayId: string) => void;
  moveStopToDay: (dayId: string, index: number, targetDayId: string, copyPlace: boolean) => void;
  updateTrip: (patch: Partial<TripDocument["trip"]>) => void;
  setNavigation: (dayId: string, navigation: Navigation) => void;
  replaceDocument: (document: TripDocument) => void;
  undo: () => void;
  redo: () => void;
  save: () => Promise<void>;
  discardDraftAndReload: () => Promise<void>;
  restoreVersion: (versionID: string) => Promise<void>;
  clearError: () => void;
}

const clone = <T,>(value: T): T => structuredClone(value);
const dayColors = ["#3977A8", "#D6603D", "#278367", "#7857A6", "#1686A4", "#9C5B24", "#9A4D6D", "#4F6775"];

function navKey(document: TripDocument, dayId: string) {
  return `${document.trip.id}::${dayId}`;
}

function markNavigationStale(document: TripDocument, dayId: string) {
  const key = navKey(document, dayId);
  const navigation = document.navigation[key];
  if (navigation) document.navigation[key] = { ...navigation, status: "stale", profile: undefined, peaks: undefined, elevationStatus: undefined, elevationSource: undefined, elevationUpdatedAt: undefined };
}

function rekeyNavigation(navigation: TripDocument["navigation"], fromID: string, toID: string) {
  return Object.fromEntries(Object.entries(navigation).map(([key, value]) => {
    const dayID = key.startsWith(`${fromID}::`) ? key.slice(fromID.length + 2) : value.dayId;
    return [`${toID}::${dayID}`, { ...value, routeId: toID }];
  }));
}

function draftKey(id: string) {
  return `shanhe:draft:${id}`;
}

function persistDraft(document: TripDocument, baseRevision: string) {
  try {
    localStorage.setItem(draftKey(document.trip.id), JSON.stringify({ document, baseRevision, updatedAt: new Date().toISOString() }));
  } catch {
    // Quota or privacy mode errors must not interrupt editing.
  }
}

function restoreDraft(id: string): { document: TripDocument; baseRevision: string } | null {
  try {
    const value = JSON.parse(localStorage.getItem(draftKey(id)) ?? "null");
    return value?.document?.trip?.id === id && typeof value.baseRevision === "string" ? value : null;
  } catch {
    return null;
  }
}

function normalizeDay(day: Day) {
  day.stops.forEach((stop, index) => (stop.sequence = index));
  day.segments = day.stops.slice(1).map((stop, index) => [day.stops[index].node, stop.node]);
}

function addCalendarDays(value: string, amount: number) {
  const date = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return undefined;
  date.setUTCDate(date.getUTCDate() + amount);
  return date.toISOString().slice(0, 10);
}

function mutate(set: (updater: (state: EditorState) => Partial<EditorState>) => void, recipe: (draft: TripDocument) => void) {
  set((state) => {
    if (!state.document) return {};
    const previous = clone(state.document);
    const next = clone(state.document);
    recipe(next);
    persistDraft(next, state.revision);
    const saving = state.saveState === "saving";
    return {
      document: next,
      history: [...state.history.slice(-49), previous],
      future: [],
      saveState: saving ? "saving" : "dirty",
      dirtyDuringSave: saving,
      error: null
    };
  });
}

function placeID(name: string, nodes: Record<string, Place>) {
  const readable = name.toLowerCase().normalize("NFKD").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 40);
  const base = readable || `place-${stableHash(name)}`;
  let candidate = base;
  let suffix = 2;
  while (nodes[candidate]) candidate = `${base}-${suffix++}`;
  return candidate;
}

function repairInvalidPlaceIDs(document: TripDocument) {
  const replacements = new Map<string, string>();
  const occupied: Record<string, Place> = {};
  for (const [id, place] of Object.entries(document.trip.nodes)) {
    if (validEntityID(id)) occupied[id] = place;
  }
  for (const [id, place] of Object.entries(document.trip.nodes)) {
    if (validEntityID(id)) continue;
    const next = placeID(place.name, occupied);
    occupied[next] = place;
    replacements.set(id, next);
    delete document.trip.nodes[id];
    document.trip.nodes[next] = place;
  }
  if (!replacements.size) return false;
  for (const day of document.trip.days) {
    for (const stop of day.stops) stop.node = replacements.get(stop.node) ?? stop.node;
    normalizeDay(day);
  }
  for (const navigation of Object.values(document.navigation)) {
    for (const segment of navigation.segments ?? []) {
      segment.fromNode = replacements.get(segment.fromNode) ?? segment.fromNode;
      segment.toNode = replacements.get(segment.toNode) ?? segment.toNode;
    }
  }
  return true;
}

function validEntityID(value: string) {
  return /^[A-Za-z0-9_-]{1,80}$/.test(value);
}

function stableHash(value: string) {
  let hash = 2166136261;
  for (const character of value) {
    hash ^= character.codePointAt(0) ?? 0;
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
}

const elevationTimers = new Map<string, ReturnType<typeof setTimeout>>();

function scheduleElevationLookup(
  set: (updater: (state: EditorState) => Partial<EditorState>) => void,
  get: () => EditorState,
  id: string,
  coordinate: Coordinate,
  replaceExisting = false
) {
  const pending = elevationTimers.get(id);
  if (pending) clearTimeout(pending);
  set((state) => ({ elevationLookups: { ...state.elevationLookups, [id]: "loading" } }));
  elevationTimers.set(id, setTimeout(() => {
    elevationTimers.delete(id);
    void api.elevation(coordinate).then((result) => {
      const current = get().document?.trip.nodes[id];
      if (!current) {
        set((state) => ({ elevationLookups: withoutKey(state.elevationLookups, id) }));
        return;
      }
      if (!sameCoordinate(current.coord, coordinate)) return;
      if (!replaceExisting && current.elevation !== undefined) {
        set((state) => ({ elevationLookups: withoutKey(state.elevationLookups, id) }));
        return;
      }
      mutate(set, (document) => {
        const place = document.trip.nodes[id];
        if (!place || !sameCoordinate(place.coord, coordinate) || (!replaceExisting && place.elevation !== undefined)) return;
        document.trip.nodes[id] = { ...place, elevation: result.elevation, elevationSource: result.source, elevationUpdatedAt: result.queriedAt };
      });
      set((state) => ({ elevationLookups: withoutKey(state.elevationLookups, id) }));
    }).catch(() => {
      const current = get().document?.trip.nodes[id];
      if (current && sameCoordinate(current.coord, coordinate)) set((state) => ({ elevationLookups: { ...state.elevationLookups, [id]: "failed" } }));
    });
  }, 120));
}

function sameCoordinate(left: Coordinate, right: Coordinate) {
  return Math.abs(left[0] - right[0]) < 0.0000001 && Math.abs(left[1] - right[1]) < 0.0000001;
}

function withoutKey<T>(values: Record<string, T>, key: string) {
  const next = { ...values };
  delete next[key];
  return next;
}

function updateSummary(items: TripSummary[], document: TripDocument, revision: string) {
  const next: TripSummary = { id: document.trip.id, name: document.trip.name, short: document.trip.short, accent: document.trip.accent, status: document.trip.status, visibility: document.trip.visibility, dayCount: document.trip.days.length, placeCount: Object.keys(document.trip.nodes).length, revision };
  return items.map((item) => item.id === next.id ? next : item);
}

export const useEditor = create<EditorState>((set, get) => ({
  summaries: [],
  document: null,
  revision: "",
  selectedDayId: null,
  selectedPlaceId: null,
  history: [],
  future: [],
  saveState: "saved",
  dirtyDuringSave: false,
  loading: true,
  error: null,
  elevationLookups: {},

  load: async (preferredId) => {
    set({ loading: true, error: null });
    try {
      const summaries = await api.trips();
      set({ summaries });
      const id = preferredId && summaries.some((item) => item.id === preferredId) ? preferredId : summaries[0]?.id;
      if (id) await get().openTrip(id);
      else set({ loading: false });
    } catch (error) {
      set({ loading: false, error: error instanceof Error ? error.message : "无法载入路线" });
    }
  },

  createTrip: async (name, startDate, mode = "driving") => {
    set({ loading: true, error: null });
    const id = `trip-${crypto.randomUUID()}`;
    const document: TripDocument = { trip: { id, name: name.trim(), short: "", accent: "#3977A8", status: "DRAFT", visibility: "PRIVATE", nodes: {}, days: [{ id: "D1", name: "第 1 天", date: startDate || undefined, departureTime: "09:00", mode, color: "#3977A8", stops: [] }] }, navigation: {} };
    try {
      const response = await api.createTrip(document);
      const summaries = await api.trips();
      set({ summaries, document: response.data, revision: response.meta?.revision ?? "", selectedDayId: "D1", selectedPlaceId: null, history: [], future: [], saveState: "saved", dirtyDuringSave: false, loading: false, elevationLookups: {} });
      localStorage.setItem("shanhe:last-trip", id);
      localStorage.removeItem(draftKey(id));
    } catch (error) {
      set({ loading: false, error: error instanceof Error ? error.message : "无法创建路线" });
    }
  },

  duplicateTrip: async () => {
    const source = get().document;
    if (!source) return;
    set({ loading: true, error: null });
    const document = clone(source);
    const sourceID = document.trip.id;
    document.trip.id = `trip-${crypto.randomUUID()}`;
    document.navigation = rekeyNavigation(document.navigation, sourceID, document.trip.id);
    document.trip.name = `${source.trip.name} · 副本`;
    document.trip.source = sourceID;
    document.trip.status = "DRAFT";
    document.trip.visibility = "PRIVATE";
    try {
      const response = await api.createTrip(document);
      const summaries = await api.trips();
      set({ summaries, document: response.data, revision: response.meta?.revision ?? "", selectedDayId: document.trip.days[0]?.id ?? null, selectedPlaceId: null, history: [], future: [], saveState: "saved", dirtyDuringSave: false, loading: false, elevationLookups: {} });
      localStorage.setItem("shanhe:last-trip", document.trip.id);
      localStorage.removeItem(draftKey(document.trip.id));
    } catch (error) {
      set({ loading: false, error: error instanceof Error ? error.message : "无法复制路线" });
    }
  },

  deleteTrip: async () => {
    const current = get().document;
    if (!current) return;
    set({ loading: true, error: null });
    try {
      await api.deleteTrip(current.trip.id, get().revision);
      localStorage.removeItem(draftKey(current.trip.id));
      const summaries = get().summaries.filter((item) => item.id !== current.trip.id);
      set({ summaries, document: null, revision: "", selectedDayId: null, selectedPlaceId: null, history: [], future: [], saveState: "saved", dirtyDuringSave: false, loading: false });
      if (summaries[0]) await get().openTrip(summaries[0].id);
    } catch (error) {
      set({ loading: false, error: error instanceof Error ? error.message : "无法删除路线" });
    }
  },

  openTrip: async (id) => {
    set({ loading: true, error: null });
    try {
      const response = await api.trip(id);
      const revision = response.meta?.revision ?? "";
      const local = restoreDraft(id);
      const document = clone(local?.document ?? response.data);
      const repairedPlaceIDs = repairInvalidPlaceIDs(document);
      const conflicted = !!local && local.baseRevision !== revision;
      if (repairedPlaceIDs) persistDraft(document, revision);
      set({ document, revision, selectedDayId: document.trip.days[0]?.id ?? null, selectedPlaceId: null, history: [], future: [], saveState: conflicted ? "conflict" : local || repairedPlaceIDs ? "dirty" : "saved", dirtyDuringSave: false, loading: false, error: conflicted ? "本机草稿基于旧版本，请刷新服务端内容或另存为副本。" : null, elevationLookups: {} });
      localStorage.setItem("shanhe:last-trip", id);
    } catch (error) {
      set({ loading: false, error: error instanceof Error ? error.message : "无法打开路线" });
    }
  },

  selectDay: (selectedDayId) => set({ selectedDayId, selectedPlaceId: null }),
  selectPlace: (selectedPlaceId) => set({ selectedPlaceId }),

  updatePlace: (id, patch) => {
    mutate(set, (document) => {
      const place = { ...document.trip.nodes[id], ...patch };
      if (patch.coord && !Object.prototype.hasOwnProperty.call(patch, "elevation")) {
        delete place.elevation;
        delete place.elevationSource;
        delete place.elevationUpdatedAt;
      }
      document.trip.nodes[id] = place;
      if (patch.coord) for (const day of document.trip.days) {
        if (!day.stops.some((stop) => stop.node === id)) continue;
        const key = navKey(document, day.id);
        const navigation = document.navigation[key];
        if (navigation) document.navigation[key] = { ...navigation, status: "stale", profile: undefined, peaks: undefined, elevationStatus: undefined, elevationSource: undefined, elevationUpdatedAt: undefined };
      }
    });
    if (patch.coord && !Object.prototype.hasOwnProperty.call(patch, "elevation")) scheduleElevationLookup(set, get, id, patch.coord);
    if (Object.prototype.hasOwnProperty.call(patch, "elevation")) {
      set((state) => ({ elevationLookups: withoutKey(state.elevationLookups, id) }));
    }
  },

  refreshElevation: (id) => {
    const coordinate = get().document?.trip.nodes[id]?.coord;
    if (coordinate) scheduleElevationLookup(set, get, id, coordinate, true);
  },

  addPlace: (place, requestedDayId) => {
    let addedID = "";
    mutate(set, (document) => {
      const dayId = requestedDayId ?? get().selectedDayId ?? document.trip.days[0]?.id;
      let day = document.trip.days.find((candidate) => candidate.id === dayId);
      if (!day) {
        day = { id: "D1", name: "第 1 天", mode: "driving", stops: [] };
        document.trip.days.push(day);
      }
      const id = placeID(place.name, document.trip.nodes);
      addedID = id;
      document.trip.nodes[id] = place;
      day.stops.push({ sequence: day.stops.length, node: id, tags: ["途经点"], dwellMinutes: day.stops.length ? 30 : 0 });
      normalizeDay(day);
      markNavigationStale(document, day.id);
      queueMicrotask(() => set({ selectedPlaceId: id, selectedDayId: day?.id ?? null }));
    });
    if (addedID && place.elevation === undefined) scheduleElevationLookup(set, get, addedID, place.coord);
  },

  removeStop: (dayId, index) => mutate(set, (document) => {
    const day = document.trip.days.find((candidate) => candidate.id === dayId);
    if (!day || !day.stops[index]) return;
    const [removed] = day.stops.splice(index, 1);
    normalizeDay(day);
    markNavigationStale(document, dayId);
    const stillUsed = document.trip.days.some((candidate) => candidate.stops.some((stop) => stop.node === removed.node));
    if (!stillUsed) delete document.trip.nodes[removed.node];
    queueMicrotask(() => set({ selectedPlaceId: null }));
  }),

  reorderStop: (dayId, from, to) => mutate(set, (document) => {
    const day = document.trip.days.find((candidate) => candidate.id === dayId);
    if (!day || from === to || from < 0 || to < 0 || from >= day.stops.length || to >= day.stops.length) return;
    const [stop] = day.stops.splice(from, 1);
    day.stops.splice(to, 0, stop);
    normalizeDay(day);
    markNavigationStale(document, dayId);
  }),

  updateStop: (dayId, index, patch) => mutate(set, (document) => {
    const day = document.trip.days.find((candidate) => candidate.id === dayId);
    if (day?.stops[index]) day.stops[index] = { ...day.stops[index], ...patch };
  }),

  updateDay: (dayId, patch) => mutate(set, (document) => {
    const day = document.trip.days.find((candidate) => candidate.id === dayId);
    if (day) Object.assign(day, patch);
    if (patch.mode) markNavigationStale(document, dayId);
  }),

  addDay: () => mutate(set, (document) => {
    const used = new Set(document.trip.days.map((day) => day.id));
    let index = document.trip.days.length + 1;
    while (used.has(`D${index}`)) index++;
    const previous = document.trip.days.at(-1);
    const nextDate = previous?.date ? addCalendarDays(previous.date, 1) : undefined;
    const day: Day = { id: `D${index}`, name: `第 ${index} 天`, date: nextDate, departureTime: previous?.departureTime ?? "09:00", mode: previous?.mode ?? "driving", color: dayColors[document.trip.days.length % dayColors.length], stops: [] };
    document.trip.days.push(day);
    queueMicrotask(() => set({ selectedDayId: day.id, selectedPlaceId: null }));
  }),

  moveDay: (dayId, direction) => mutate(set, (document) => {
    const index = document.trip.days.findIndex((day) => day.id === dayId);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= document.trip.days.length) return;
    const [day] = document.trip.days.splice(index, 1);
    document.trip.days.splice(target, 0, day);
  }),

  deleteDay: (dayId) => mutate(set, (document) => {
    const index = document.trip.days.findIndex((day) => day.id === dayId);
    if (index < 0 || document.trip.days.length <= 1) return;
    const [removedDay] = document.trip.days.splice(index, 1);
    const removedNodeIds = new Set(removedDay.stops.map((stop) => stop.node));
    for (const nodeId of removedNodeIds) {
      const stillUsed = document.trip.days.some((day) => day.stops.some((stop) => stop.node === nodeId));
      if (!stillUsed) delete document.trip.nodes[nodeId];
    }
    delete document.navigation[navKey(document, dayId)];
    queueMicrotask(() => set({ selectedDayId: document.trip.days[Math.min(index, document.trip.days.length - 1)]?.id ?? null, selectedPlaceId: null }));
  }),

  moveStopToDay: (dayId, index, targetDayId, copyPlace) => mutate(set, (document) => {
    const source = document.trip.days.find((day) => day.id === dayId);
    const target = document.trip.days.find((day) => day.id === targetDayId);
    const original = source?.stops[index];
    if (!source || !target || !original || source === target) return;
    let stop = clone(original);
    if (copyPlace) {
      const originalPlace = document.trip.nodes[stop.node];
      const newId = placeID(originalPlace?.name ?? "地点", document.trip.nodes);
      document.trip.nodes[newId] = clone(originalPlace);
      stop.node = newId;
    } else {
      source.stops.splice(index, 1);
      normalizeDay(source);
      markNavigationStale(document, source.id);
    }
    target.stops.push({ ...stop, sequence: target.stops.length });
    normalizeDay(target);
    markNavigationStale(document, target.id);
    queueMicrotask(() => set({ selectedDayId: target.id, selectedPlaceId: stop.node }));
  }),

  updateTrip: (patch) => mutate(set, (document) => Object.assign(document.trip, patch)),
  setNavigation: (dayId, navigation) => mutate(set, (document) => { document.navigation[navKey(document, dayId)] = navigation; }),
  replaceDocument: (replacement) => mutate(set, (document) => {
    const currentID = document.trip.id;
    const importedID = replacement.trip.id;
    Object.assign(document, clone(replacement));
    document.trip.id = currentID;
    document.navigation = rekeyNavigation(document.navigation, importedID, currentID);
    document.trip.days.forEach(normalizeDay);
  }),

  undo: () => set((state) => {
    if (!state.document || !state.history.length) return {};
    const previous = state.history[state.history.length - 1];
    persistDraft(previous, state.revision);
    const saving = state.saveState === "saving";
    return { document: clone(previous), history: state.history.slice(0, -1), future: [clone(state.document), ...state.future].slice(0, 50), saveState: saving ? "saving" : "dirty", dirtyDuringSave: saving };
  }),
  redo: () => set((state) => {
    if (!state.document || !state.future.length) return {};
    const next = state.future[0];
    persistDraft(next, state.revision);
    const saving = state.saveState === "saving";
    return { document: clone(next), history: [...state.history, clone(state.document)].slice(-50), future: state.future.slice(1), saveState: saving ? "saving" : "dirty", dirtyDuringSave: saving };
  }),

  save: async () => {
    const { document: currentDocument, revision, saveState } = get();
    if (!currentDocument || saveState === "saving" || saveState === "saved") return;
    const repairedDocument = clone(currentDocument);
    const repairedPlaceIDs = repairInvalidPlaceIDs(repairedDocument);
    const document = repairedPlaceIDs ? repairedDocument : currentDocument;
    set({ document, saveState: "saving", dirtyDuringSave: false, error: null });
    try {
      const response = await api.saveTrip(document, revision);
      localStorage.removeItem(draftKey(document.trip.id));
      const nextRevision = response.meta?.revision ?? revision;
      set((state) => {
        // A route calculation or another edit may finish while this request is
        // in flight. Preserve that newer document and save it against the new
        // server revision instead of overwriting it with the older response.
        if (state.document && (state.dirtyDuringSave || state.document !== document)) {
          persistDraft(state.document, nextRevision);
          return { revision: nextRevision, summaries: updateSummary(state.summaries, state.document, nextRevision), saveState: "dirty", dirtyDuringSave: false };
        }
        return { document: response.data, revision: nextRevision, summaries: updateSummary(state.summaries, response.data, nextRevision), saveState: "saved", dirtyDuringSave: false };
      });
    } catch (error) {
      const conflict = error instanceof ApiProblem && error.code === "revision_conflict";
      set({ saveState: conflict ? "conflict" : "offline", dirtyDuringSave: false, error: error instanceof Error ? error.message : "保存失败" });
    }
  },
  discardDraftAndReload: async () => {
    const id = get().document?.trip.id;
    if (!id) return;
    localStorage.removeItem(draftKey(id));
    await get().openTrip(id);
  },
  restoreVersion: async (versionID) => {
    const { document, revision } = get();
    if (!document) return;
    set({ loading: true, error: null });
    try {
      const response = await api.restoreVersion(document.trip.id, versionID, revision);
      const nextRevision = response.meta?.revision ?? revision;
      localStorage.removeItem(draftKey(document.trip.id));
      set((state) => ({ document: response.data, revision: nextRevision, summaries: updateSummary(state.summaries, response.data, nextRevision), selectedDayId: response.data.trip.days[0]?.id ?? null, selectedPlaceId: null, history: [...state.history.slice(-49), clone(document)], future: [], saveState: "saved", dirtyDuringSave: false, loading: false }));
    } catch (error) {
      set({ loading: false, error: error instanceof Error ? error.message : "无法恢复历史版本" });
    }
  },
  clearError: () => set({ error: null })
}));

export function selectedDay(document: TripDocument | null, id: string | null) {
  return document?.trip.days.find((day) => day.id === id) ?? null;
}

export function directDistanceKm(a: Coordinate, b: Coordinate) {
  const rad = (value: number) => (value * Math.PI) / 180;
  const dLat = rad(b[0] - a[0]);
  const dLng = rad(b[1] - a[1]);
  const value = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a[0])) * Math.cos(rad(b[0])) * Math.sin(dLng / 2) ** 2;
  return 6371 * 2 * Math.atan2(Math.sqrt(value), Math.sqrt(1 - value));
}
